import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  OffsetOptions,
  Placement,
  size,
} from '@floating-ui/dom';
import { TmElement, watch } from '@thiememeulenhoff/lit-sdk';
import { html } from 'lit';
import {
  customElement,
  property,
  query,
  queryAssignedElements,
} from 'lit/decorators.js';
import { lock, unlock } from 'tua-body-scroll-lock';
import { tagName } from '../../helpers/element.helper';
import './dropdown-arrow/dropdown-arrow.component';
import { styles } from './dropdown.styles';
import { DROPZONE_SELECTED } from '../drag-and-drop/shared/events/drag-drop.events';

const tag = tagName('dropdown');

@customElement(tag)
export class DropdownComponent extends TmElement {
  static override styles = styles;

  @property({ type: Boolean, reflect: true })
  open = false;

  @query('.trigger')
  triggerElement!: HTMLElement;

  @query('.content-container')
  contentElement?: HTMLElement;

  @query('.main')
  mainElement!: HTMLElement;

  @query('.arrow')
  arrowElement!: HTMLElement;

  @queryAssignedElements({
    slot: 'trigger',
    selector: 'a-button, a-icon-button, button',
  })
  triggerButtons!: Array<HTMLElement>;

  @property({ type: String })
  placement: Placement = 'bottom-start';

  @property({ type: Boolean, reflect: true })
  disabled?: boolean;

  @property({ type: Boolean })
  arrow?: boolean;

  @property({ type: Boolean })
  radius?: boolean;

  @property({ type: Number })
  offset?: number;

  @property({ type: Boolean })
  backdrop = false;

  cleanup?: () => void;

  #isLocked = false; // Track if body scroll is currently locked

  @watch('open', { waitUntilFirstUpdate: true })
  handleOpenChange() {
    if (this.open) {
      this.#addOpenListeners();
      this.dispatchEvent(
        new CustomEvent('dropdown-opened', { bubbles: true, composed: true }),
      );

      this.triggerButtons.forEach((button) => {
        button.setAttribute('active', 'true');
      });
    } else {
      this.#removeOpenListeners();
      this.dispatchEvent(
        new CustomEvent('dropdown-closed', { bubbles: true, composed: true }),
      );

      this.triggerButtons.forEach((button) => {
        button.removeAttribute('active');
      });
    }
  }

  override render() {
    return html`
      <slot
        name="trigger"
        class="trigger"
        part="trigger"
        data-test="trigger"
        @click=${this.toggle}
        @keydown=${this.handleKeyDown}
        tabindex=${this.disabled ? '-1' : '0'}
      ></slot>
      <div
        popover="manual"
        class="content-container"
        ?arrow=${this.arrow}
        ?open=${this.open}
        ?backdrop=${this.backdrop}
        ?radius=${this.radius}
      >
        ${this.#renderArrow()}
        <div part="content" class="content">
          <div class="header"><slot name="header"></slot></div>
          <div class="main"><slot></slot></div>
          <div class="footer"><slot name="footer"></slot></div>
        </div>
      </div>
    `;
  }

  #renderArrow() {
    if (this.arrow) {
      return html`<div class="arrow">
        <a-dropdown-arrow></a-dropdown-arrow>
      </div>`;
    }
    return '';
  }

  toggle() {
    if (this.disabled) {
      return;
    }

    if (!this.open) {
      this.show();
      return;
    }

    this.hide();
  }

  // Prevent error on older browsers
  #showPopover() {
    if (
      this.contentElement &&
      typeof this.contentElement?.showPopover === 'function'
    ) {
      this.contentElement.showPopover();
    }
  }

  // Prevent error on older browsers
  #hidePopover() {
    if (
      this.contentElement &&
      typeof this.contentElement?.hidePopover === 'function'
    ) {
      this.contentElement.hidePopover();
    }
  }

  show() {
    this.open = true;
    this.#showPopover();

    this.#lockOrUnlock();
  }

  hide() {
    this.open = false;

    if (!this.backdrop) {
      this.#hidePopover();
      this.#lockOrUnlock();
      return;
    }

    // Backdrop is closed with an animation
    this.contentElement?.addEventListener('animationend', (event) => {
      if (event.animationName === 'dropdown-fade-out') {
        this.contentElement?.classList.remove('closing');
        this.classList.remove('closing');
        this.#hidePopover();
        this.#lockOrUnlock();
      }
    });

    this.contentElement?.classList.add('closing');
    this.classList.add('closing');
  }

  /**
   * Update the position of the dropdown content based on the trigger element using Floating UI.
   */
  updatePosition() {
    const offsetValue: OffsetOptions = {
      crossAxis: this.offset,
    };

    if (!this.open) {
      return;
    }

    const { placement } = this;

    const middleware = [
      size({
        apply: ({ elements }) => {
          const reference = elements.reference as HTMLElement;

          this.style.setProperty(
            '--reference-width',
            `${reference.clientWidth}px`,
          );
        },
      }),
      flip(),
    ];

    if (this.arrow) {
      middleware.push(arrow({ element: this.arrowElement }));
      offsetValue.mainAxis = 30;
    }

    middleware.unshift(offset(offsetValue));

    if (!this.contentElement) {
      return;
    }
    computePosition(this.triggerElement, this.contentElement, {
      placement: `${placement}`,
      middleware,
    }).then(({ x, y, middlewareData, placement }) => {
      if (!this.contentElement) {
        return;
      }

      const maxX = Math.max(8, x);

      // Prevent dropdown from being positioned above the top
      const maxY = Math.max(8, y - window.scrollY);

      Object.assign(this.contentElement.style, {
        'inset-inline-start': `${maxX}px`,
        'inset-block-start': `${y - window.scrollY}px`,
      });

      const availableWidth = document.documentElement.clientWidth - maxX - 8;

      // Available height based on viewport and current top
      const availableHeight =
        placement && placement.startsWith('top')
          ? this.triggerElement.getBoundingClientRect().top -
            this.triggerElement.getBoundingClientRect().height
          : document.documentElement.clientHeight - maxY - 8;

      this.style.setProperty(
        '--auto-size-available-height',
        `${availableHeight}px`,
      );

      this.style.setProperty(
        '--auto-size-available-width',
        `${availableWidth}px`,
      );

      if (middlewareData.arrow) {
        const { x } = middlewareData.arrow;
        Object.assign(this.arrowElement.style, {
          'inset-inline-start': x != null ? `${x - 10}px` : '',
        });
      }
    });
  }

  handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      const target = e.target as Element;

      const isButton =
        target?.tagName === 'BUTTON' || target?.tagName === 'A-BUTTON';

      // buttons already call the toggle event through the click event with enter
      if (!isButton) {
        this.toggle();
      }
    }
  }

  override async connectedCallback() {
    super.connectedCallback();

    await this.updateComplete;

    if (this.contentElement) {
      this.cleanup = autoUpdate(
        this.triggerElement,
        this.contentElement,
        this.updatePosition.bind(this),
      );
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanup?.();
    this.hide();
    this.#lockOrUnlock();
  }

  #handleDocumentEvent = (event: Event) => {
    if (!this.contentElement) {
      return;
    }
    const path = event.composedPath();
    if (
      (this &&
        !path.includes(this.contentElement) &&
        !path.includes(this.triggerElement)) ||
      (event instanceof KeyboardEvent && event.key === 'Escape')
    ) {
      this.hide();
    }
  };

  #addOpenListeners() {
    document.addEventListener('mousedown', this.#handleDocumentEvent);
    document.addEventListener('keydown', this.#handleDocumentEvent);
    document.addEventListener(DROPZONE_SELECTED, this.#handleDocumentEvent);
  }

  #removeOpenListeners() {
    document.removeEventListener('mousedown', this.#handleDocumentEvent);
    document.removeEventListener('keydown', this.#handleDocumentEvent);
    document.removeEventListener(DROPZONE_SELECTED, this.#handleDocumentEvent);
  }

  #lockOrUnlock() {
    if (!this.backdrop) {
      return;
    }

    if (this.open && !this.#isLocked) {
      lock(this.mainElement);
      this.#isLocked = true;
    } else if (!this.open && this.#isLocked) {
      unlock(this.mainElement);
      this.#isLocked = false;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-dropdown': DropdownComponent;
  }
}
